// ============================================================================
// DataService - Central HTTP Gateway for Arbitech Data Operations
// ============================================================================
// Replaces V1's svc-dbcalls.js (dbCalls service). All data flows through here.
// Four endpoints: dgGet (action array), dgAction (named action), dsSave (upsert/delete),
// getData (email/print dispatch). Backend routes to ProcessData via DataGetterController.
// ============================================================================

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Central HTTP service for Arbitech data operations.
 * Mirrors the V1 dbCalls service (svc-dbcalls.js):
 *   dgGet  -> POST /api/datagetter/dg   (JSON action array)
 *   dgAction -> POST /api/datagetter/action (string action name)
 *   dsSave -> POST /api/datagetter/ds   (save instruction array)
 *   getData -> POST /api/datagetter/getdata (email/print dispatch)
 */
@Injectable()
export class DataService {

  constructor(private http: HttpClient) {}

  // Primary data retrieval. Send array of action objects.
  // Example: dgGet([{ action: 'QuotesGet', returnas: 'BASE' }])
  // Receives as text and parses manually: side-effect actions with
  // `returnas: ''` (FavoriteAdd, QuoteSetPONr, etc.) return an empty or
  // plain-text body that HttpClient's implicit JSON parser would surface
  // as a fake error, reverting optimistic UI updates. Empty → null,
  // valid JSON → parsed, anything else (e.g. "OK", "1", a status string)
  // returned as-is.
  dgGet(actions: any[]): Observable<any> {
    return this.http.post('/api/datagetter/dg', actions, { responseType: 'text' }).pipe(
      map((text: string) => {
        if (!text) return null;
        try { return JSON.parse(text); }
        catch { return text; }
      })
    );
  }

  // Action-based call with full server-side context (initialDataLoad, getNotificationList, etc.)
  dgAction(action: string): Observable<any> {
    return this.http.post<any>('/api/datagetter/action', JSON.stringify(action), {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  // Save data. Send array of table/type/rows instructions.
  dsSave(saveData: any[]): Observable<any> {
    return this.http.post<any>('/api/datagetter/ds', saveData, { responseType: 'text' as any });
  }

  // Dispatch for email/print operations (QuoteEmail, OrderPrint, etc.)
  getData(actions: any[]): Observable<any> {
    return this.http.post('/api/datagetter/getdata', actions, { responseType: 'text' });
  }
}
